import React, { useState } from "react";
import {
  ExclamationTriangleIcon,
  PauseIcon,
  PlayIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "./LoadingSpinner";

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmStyle = "danger",
  loading = false,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
            {title}
          </h3>
          <p className="text-sm text-gray-500 text-center mb-6">{message}</p>
          <div className="flex justify-center space-x-4">
            <button
              type="button"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
                confirmStyle === "danger"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-primary-600 text-white hover:bg-primary-700"
              }`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading && <LoadingSpinner size="sm" />}
              <span>{confirmText}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BillingActions = ({ subscription, onUpdate, onMessage }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const handleCancelSubscription = async () => {
    try {
      setActionLoading("cancel");
      const response = await fetch("/api/subscriptions/cancel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ immediate: false }),
      });

      if (!response.ok) {
        throw new Error("Failed to cancel subscription");
      }

      setShowCancelModal(false);
      onMessage({
        type: "success",
        text: "Subscription cancelled successfully. Access continues until the end of your billing period.",
      });
      onUpdate();
    } catch (error) {
      onMessage({
        type: "error",
        text: error.message || "Failed to cancel subscription",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePauseSubscription = async () => {
    try {
      setActionLoading("pause");
      const response = await fetch("/api/subscriptions/pause", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to pause subscription");
      }

      setShowPauseModal(false);
      onMessage({
        type: "success",
        text: "Subscription paused successfully. You can reactivate it anytime.",
      });
      onUpdate();
    } catch (error) {
      onMessage({
        type: "error",
        text: error.message || "Failed to pause subscription",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setActionLoading("reactivate");
      const response = await fetch("/api/subscriptions/reactivate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to reactivate subscription");
      }

      setShowReactivateModal(false);
      onMessage({
        type: "success",
        text: "Subscription reactivated successfully!",
      });
      onUpdate();
    } catch (error) {
      onMessage({
        type: "error",
        text: error.message || "Failed to reactivate subscription",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadData = async () => {
    try {
      setActionLoading("download");
      const response = await fetch("/api/subscriptions/export-data", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export data");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `dz-metrics-data-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      onMessage({
        type: "success",
        text: "Data export started successfully! Download will begin shortly.",
      });
    } catch (error) {
      onMessage({
        type: "error",
        text: error.message || "Failed to export data",
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (!subscription) return null;

  const isActive = subscription.status === "active";
  const isPaused = subscription.status === "paused";
  const isCancelled =
    subscription.status === "canceled" || subscription.status === "cancelled";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Subscription Actions
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pause/Resume Subscription */}
        {isActive && (
          <button
            onClick={() => setShowPauseModal(true)}
            disabled={actionLoading === "pause"}
            className="flex items-center justify-center space-x-2 p-4 border border-yellow-300 rounded-lg text-yellow-700 hover:bg-yellow-50 transition-colors disabled:opacity-50"
          >
            {actionLoading === "pause" ? (
              <LoadingSpinner size="sm" />
            ) : (
              <PauseIcon className="h-5 w-5" />
            )}
            <span>Pause Subscription</span>
          </button>
        )}

        {isPaused && (
          <button
            onClick={() => setShowReactivateModal(true)}
            disabled={actionLoading === "reactivate"}
            className="flex items-center justify-center space-x-2 p-4 border border-green-300 rounded-lg text-green-700 hover:bg-green-50 transition-colors disabled:opacity-50"
          >
            {actionLoading === "reactivate" ? (
              <LoadingSpinner size="sm" />
            ) : (
              <PlayIcon className="h-5 w-5" />
            )}
            <span>Reactivate Subscription</span>
          </button>
        )}

        {/* Cancel Subscription */}
        {isActive && (
          <button
            onClick={() => setShowCancelModal(true)}
            disabled={actionLoading === "cancel"}
            className="flex items-center justify-center space-x-2 p-4 border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {actionLoading === "cancel" ? (
              <LoadingSpinner size="sm" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5" />
            )}
            <span>Cancel Subscription</span>
          </button>
        )}

        {/* Download Data */}
        <button
          onClick={handleDownloadData}
          disabled={actionLoading === "download"}
          className="flex items-center justify-center space-x-2 p-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          {actionLoading === "download" ? (
            <LoadingSpinner size="sm" />
          ) : (
            <ArrowDownTrayIcon className="h-5 w-5" />
          )}
          <span>Download All Data</span>
        </button>
      </div>

      {/* Status Information */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Important Information
        </h3>
        <ul className="text-sm text-gray-600 space-y-1">
          {isActive && (
            <>
              <li>
                • Pausing your subscription will stop all data collection and
                API access
              </li>
              <li>• You can reactivate your subscription at any time</li>
              <li>
                • Canceling will continue service until your next billing date
              </li>
            </>
          )}
          {isPaused && (
            <>
              <li>• Your subscription is currently paused</li>
              <li>• Data collection and API access are disabled</li>
              <li>• Reactivate to resume full service</li>
            </>
          )}
          {isCancelled && (
            <>
              <li>• Your subscription has been cancelled</li>
              <li>
                • Service continues until {subscription.current_period_end}
              </li>
              <li>• You can reactivate before the end date</li>
            </>
          )}
          <li>
            • Data exports include all your analytics and account information
          </li>
        </ul>
      </div>

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You'll continue to have access until your current billing period ends."
        confirmText="Cancel Subscription"
        confirmStyle="danger"
        loading={actionLoading === "cancel"}
      />

      <ConfirmationModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={handlePauseSubscription}
        title="Pause Subscription"
        message="Your subscription will be paused and data collection will stop. You can reactivate anytime without losing your data."
        confirmText="Pause Subscription"
        confirmStyle="warning"
        loading={actionLoading === "pause"}
      />

      <ConfirmationModal
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={handleReactivateSubscription}
        title="Reactivate Subscription"
        message="This will resume your subscription and restart data collection. Billing will resume on your next billing date."
        confirmText="Reactivate Subscription"
        confirmStyle="primary"
        loading={actionLoading === "reactivate"}
      />
    </div>
  );
};

export default BillingActions;
